<template>
  <Draggable
    tag="ul"
    class="dragArea list-group w-full"
    :list="fieldList"
    ghost-class="drop-target-field"
    chosen-class="moving-field"
    :animation="200"
    group="fields"
    filter=".field-input"
    :preventOnFilter="false"
    :invert-swap="true"
    @change="addField($event)"
    :delayOnTouchOnly="true"
    :disabled="isMobile"
  >
    <li
      class="flex items-stretch"
      v-for="(field, fieldIndex) in fieldList"
      :key="field.name"
      @click="toggleFieldHighlight(field)"
    >
      <div v-if="isNestedYesNoField" class="flex flex-col w-3">
        <div
          class="h-1/2 border-b-2 border-l-2 w-3"
          :class="[
            backgroundColor === 'bg-brand-background-light-red'
              ? 'border-brand-red'
              : 'border-brand-green'
          ]"
        ></div>
        <div
          v-if="fieldIndex !== fieldList.length - 1"
          class="h-1/2 border-l-2 w-3"
          :class="[
            backgroundColor === 'bg-brand-background-light-red'
              ? 'border-brand-red'
              : 'border-brand-green'
          ]"
        ></div>
      </div>

      <div class="hidden w-full border-2 border-brand-green target-line my-2"></div>
      <div
        class="flex flex-row rounded border-4 py-3 my-3 w-full draggable-item relative"
        :class="[
          field.highlight ? 'border-brand-green' : 'border-brand-light-gray',
          backgroundColor ? backgroundColor : ''
        ]"
      >
        <div class="flex items-center cursor-pointer px-1">
          <img
            src="@/assets/icons/double-dot.svg"
            class="w-3 h-3"
            :ref="`field-${field.name}-draggable`"
          />
        </div>
        <div class="flex flex-col py-1 px-1 w-full field-content">
          <div class="flex flex-row justify-between field-input">
            <TextDisplayEditToggle
              v-if="field.field_id !== 'text_display'"
              :helpText="field.label"
              :type="'field_label'"
              @input="field.label = $event.target.value"
              :disabled="!checkFieldIsEditable(field)"
            />
            <div class="hidden md:flex w-full" v-else></div>
            <div class="hidden md:flex w-full md:w-1/3">
              <DropDown
                :disabled="!checkFieldIsEditable(field)"
                :options="
                  fieldDefaults.filter(
                    (f) =>
                      f.display_in_builder &&
                      (f.field_id !== 'agent' ||
                        (getCurrentUser.landlord.agent_field_enabled &&
                          getCurrentUser.landlord.in_company)) &&
                      !(f.field_id === 'yes_no' && !yesNoEnabled)
                  )
                "
                :placeholder="
                  fieldDefaults.find((f) => f.field_id === field.field_id)
                    ? fieldDefaults.find((f) => f.field_id === field.field_id).label
                    : 'Field Options'
                "
                @select="selectFieldType(field, $event)"
              />
            </div>
          </div>

          <div class="flex w-full py-1 pr-3 field-input">
            <FieldDisplay :field="field" mode="form_edit" />
          </div>

          <div class="flex justify-between py-2 w-full">
            <div class="flex items-center w-1/2">
              <div
                class="flex items-center mr-8 field-input"
                v-if="field.field_id !== 'text_display'"
              >
                <Switch
                  :disabled="!checkFieldIsEditable(field) || checkFieldIsRequired(field)"
                  v-model="field.required"
                  :class="[
                    field.required ? 'bg-brand-green' : 'bg-brand-dark-gray',
                    !checkFieldIsEditable(field) || checkFieldIsRequired(field) ? 'opacity-50' : ''
                  ]"
                  class="relative inline-flex items-center h-7 rounded-full w-14 mr-2"
                >
                  <span
                    :class="field.required ? 'translate-x-7.5' : 'translate-x-0.5'"
                    class="inline-block w-6 h-6 transform bg-white rounded-full"
                  />
                </Switch>
                <p :class="[field.required ? 'text-brand-black' : 'text-brand-dark-gray']">
                  Required
                </p>
              </div>
              <div
                class="hidden md:flex items-center field-input"
                :class="[
                  !checkFieldIsEditable(field) || checkFieldIsRequired(field)
                    ? 'pointer-events-none'
                    : 'cursor-pointer'
                ]"
                @click="field.summary = !field.summary"
              >
                <StarIcon
                  class="w-6 h-6 mr-1"
                  :class="[field.summary ? 'text-brand-blue' : 'text-brand-dark-gray']"
                />
                <p
                  class="text-sm"
                  :class="[field.summary ? 'text-brand-blue' : 'text-brand-dark-gray']"
                >
                  {{ field.summary ? "Added to summary" : "Add to summary" }}
                </p>
              </div>
            </div>
            <div class="flex items-center field-input justify-end w-1/2 px-2">
              <DocumentDuplicateIcon
                class="text-brand-dark-gray w-6 h-6 cursor-pointer mr-5"
                @click="cloneField(field)"
              />
              <TrashIcon
                v-if="checkFieldIsEditable(field)"
                class="text-brand-red w-6 h-6 cursor-pointer"
                @click="removeField(field)"
              />
            </div>
          </div>

          <template v-if="field.field_id === 'yes_no' && yesNoEnabled">
            <div
              v-for="(choice, choiceIndex) in field.choices"
              :key="`${field.name}-choice-${choiceIndex}`"
            >
              <div class="flex justify-between mt-5">
                <div
                  class="flex items-center px-1 py-2 border-2"
                  :class="[
                    choice.type === 'positive'
                      ? 'border-brand-green bg-brand-background-light-green'
                      : choice.type === 'negative'
                      ? 'border-brand-red bg-brand-background-light-red'
                      : ''
                  ]"
                >
                  <template v-if="choice.type === 'positive'">
                    <CheckIcon class="w-5 h-5 text-brand-green mx-1" />
                  </template>
                  <template v-if="choice.type === 'negative'">
                    <XIcon class="w-5 h-5 text-brand-red mx-1" />
                  </template>
                  <p class="mr-2">If</p>
                  <input
                    type="radio"
                    class="border-gray-300 text-brand-green focus:ring-brand-green"
                    disabled
                    checked
                  />
                  <label class="font-semibold mx-2">{{ choice.label }}</label>
                  <p class="mr-1">then ask:</p>
                </div>

                <div class="flex items-center ml-5">
                  <SecondaryButton @click="openFieldOptionModal(choice)">
                    <div class="flex items-center py-1 md:py-0 md:px-5">
                      <PlusIcon class="w-5 h-5 text-brand-green mr-1" />
                      <p>Add Field</p>
                    </div>
                  </SecondaryButton>
                </div>
              </div>

              <div
                class="flex flex-col items-center pr-2"
                :class="[
                  choice.fields.length === 0
                    ? choice.type === 'positive'
                      ? 'bg-brand-background-light-green my-2'
                      : choice.type === 'negative'
                      ? 'bg-brand-background-light-red my-2'
                      : 'my-2'
                    : ''
                ]"
              >
                <template v-if="choice.fields.length === 0">
                  <p class="text-brand-dark-gray px-2 py-3">
                    No questions asked when {{ choice.label }} is seleced. Click Add field to create
                    a new question
                  </p>
                </template>
                <FormEditDraggable
                  :fieldList="choice.fields"
                  @add="addFieldToYesNoField(choice, $event)"
                  @select_type="selectYesNoFieldType(choice, $event.field, $event.event)"
                  @remove="removeYesNoField(choice, $event)"
                  @clone="cloneYesNoField(choice, $event)"
                  :yesNoEnabled="false"
                  :backgroundColor="
                    choice.type === 'positive'
                      ? 'bg-brand-background-light-green'
                      : choice.type === 'negative'
                      ? 'bg-brand-background-light-red'
                      : ''
                  "
                  :isNestedYesNoField="true"
                />
              </div>
            </div>
          </template>
        </div>
        <template v-if="field.highlight">
          <div
            class="w-6 h-6 bg-brand-green absolute rounded-xl flex items-center justify-center -bottom-3 left-36"
            @click.stop="moveField('down', fieldIndex)"
          >
            <ChevronDownIcon class="w-5 h-5 text-white" />
          </div>
          <div
            class="w-6 h-6 bg-brand-green absolute rounded-xl flex items-center justify-center -bottom-3 left-48"
            @click.stop="moveField('up', fieldIndex)"
          >
            <ChevronUpIcon class="w-5 h-5 text-white" />
          </div>
        </template>
      </div>
    </li>
  </Draggable>
  <FieldOptionModal
    :sectionLabel="currentLabel"
    :showModal="showFieldOptionModal"
    @close="addFieldFromModal"
  />
</template>
<script>
import _ from "lodash"

import fieldDefaults from "@/constants/fieldDefaults"
import { mapGetters } from "vuex"

import {
  // EyeIcon,
  PlusIcon,
  TrashIcon,
  StarIcon,
  DocumentDuplicateIcon,
  XIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "@heroicons/vue/outline"

import { VueDraggableNext } from "vue-draggable-next"
import { Switch } from "@headlessui/vue"

import FieldOptionModal from "./FieldOptionModal.vue"

export default {
  components: {
    PlusIcon,
    TrashIcon,
    StarIcon,
    DocumentDuplicateIcon,
    Draggable: VueDraggableNext,
    Switch,
    XIcon,
    CheckIcon,
    FieldOptionModal,
    ChevronDownIcon,
    ChevronUpIcon
  },
  emits: ["add", "select_type", "remove", "clone", "highlight", "move"],
  props: {
    fieldList: {
      type: Array,
      required: false,
      default: null
    },
    yesNoEnabled: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: ""
    },
    isNestedYesNoField: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(["getCurrentUser", "getCurrentApplicationTemplate"])
  },
  mounted() {
    this.fieldDefaults = [...fieldDefaults]
  },
  methods: {
    moveField(direction, fieldIndex) {
      this.$emit("move", {
        direction,
        fieldIndex
      })
    },
    checkFieldIsEditable(field) {
      return field.editable !== false
    },
    checkFieldIsRequired(field) {
      return _.chain(this.getCurrentApplicationTemplate.selected_report_sources)
        .map("required_fields")
        .flatten()
        .uniq()
        .value()
        .includes(field.name)
    },
    addField(evt) {
      this.$emit("add", evt)
    },
    selectFieldType(field, event) {
      this.$emit("select_type", { field, event })
    },
    removeField(field) {
      this.$emit("remove", field)
    },
    cloneField(field) {
      this.$emit("clone", field)
    },
    addFieldFromModal(type) {
      if (type && type !== "yes_no") {
        const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === type))
        newField.name = `${newField.field_id}-${this.randomSlug()}`

        this.currentChoice.fields = [...this.currentChoice.fields, newField]
      }
      if (this.showFieldOptionModal) {
        this.closeFieldOptionModal()
      }
    },
    addFieldToYesNoField(choice, evt) {
      if (evt.added) {
        const newField = choice.fields[evt.added.newIndex]
        newField.name = `${newField.field_id}-${this.randomSlug()}`

        //Prevent add of yes no field to current yes no field
        if (newField.field_id === "yes_no") {
          choice.fields = [...choice.fields.filter((f) => f.name !== newField.name)]
        }
      }
    },
    selectYesNoFieldType(choice, field, newType) {
      const newField = _.cloneDeep(this.fieldDefaults.find((f) => f.field_id === newType.field_id))
      newField.name = `${newField.field_id}-${this.randomSlug()}`

      const index = _.findIndex(choice.fields, (f) => f.name === field.name)

      if (index >= 0) {
        choice.fields[index] = newField
      }
    },
    randomSlug() {
      return _.times(5, () => _.random(35).toString(36)).join("")
    },
    closeFieldOptionModal() {
      this.showFieldOptionModal = false
      this.currentField = null
      this.currentListType = ""
      this.currentLabel = ""
    },
    openFieldOptionModal(choice) {
      this.showFieldOptionModal = true
      this.currentChoice = choice
      this.currentLabel = `${choice.label} Option Field List`
    },

    removeYesNoField(choice, field) {
      if (!this.checkFieldIsRequired(field) && this.checkFieldIsEditable(field)) {
        choice.fields = [...choice.fields.filter((f) => f.name !== field.name)]
      }
    },
    cloneYesNoField(choice, field) {
      const newField = _.cloneDeep(field)
      newField.name = `${newField.field_id}-${this.randomSlug()}`
      newField.editable = true

      const index = _.findIndex(choice.fields, (f) => f.name === field.name)

      if (index >= 0) {
        choice.fields.splice(index + 1, 0, newField)
      }
    },
    toggleFieldHighlight(field) {
      if (this.isMobile && !this.isNestedYesNoField) {
        this.$emit("highlight", field)
      }
    }
  },
  data() {
    return {
      fieldDefaults: [],
      showFieldOptionModal: false,
      currentChoice: null,
      currentLabel: ""
    }
  }
}
</script>
